/** Vista tipo roadmap: recorrido completo + ficha de la parada seleccionada. */
import { Fragment, useEffect, useMemo, useState } from "react";
import { paradasConDesvio } from "@/lib/rutaHorarios";

const CATEGORIAS = [
  { id: "anotaciones", label: "Anotaciones", icon: "📝" },
  { id: "gastos", label: "Gastos", icon: "💶" },
  { id: "documentos", label: "Docs / fotos", icon: "📎" },
  { id: "ubicacion", label: "Ubicación", icon: "📍" },
];

function Pin({ n, color, active, realizada, onClick }) {
  const label = String(n).padStart(2, "0");
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative z-10 flex flex-col items-center transition ${active ? "scale-110" : "hover:scale-105"}`}
    >
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold text-white shadow-lg ${
          active ? "ring-4 ring-amber-300" : "ring-4 ring-white"
        }`}
        style={{ backgroundColor: realizada ? "#059669" : color }}
      >
        {realizada ? "✓" : label}
      </span>
      <span
        className="mt-[-2px] h-0 w-0 border-l-[8px] border-r-[8px] border-t-[12px] border-l-transparent border-r-transparent"
        style={{ borderTopColor: realizada ? "#059669" : color }}
      />
    </button>
  );
}

function HorasPin({ p }) {
  const inicial = p.hora_planificada || p.hora_visita || p.hora_estimada || "";
  const estimada = p.hora_recalculada || "";
  const misma = Boolean(inicial && estimada && inicial === estimada);
  const realizada = p.estado === "visitada";

  return (
    <div className="flex flex-col items-center gap-0.5">
      {realizada && (
        <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
          Realizada
        </span>
      )}
      {inicial ? (
        <span
          className="rounded bg-zinc-900/90 px-1.5 py-0.5 text-[10px] font-medium text-white"
          title="Hora inicial establecida"
        >
          {inicial}
        </span>
      ) : null}
      {estimada && !misma ? (
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium text-white ${
            realizada ? "bg-emerald-800" : "bg-sky-700"
          }`}
          title={realizada ? "Hora real de visita" : "Hora estimada de visita"}
        >
          {realizada ? "real" : "est."} {estimada}
        </span>
      ) : null}
    </div>
  );
}

function fmtKm(tramo) {
  if (!tramo || tramo.km == null) return null;
  return `${Number(tramo.km).toFixed(1)} km`;
}

function CardContenido({ categoria, parada, onOpen }) {
  const r = parada.resumen || {};
  if (categoria === "anotaciones") {
    const txt = (parada.notas_visita || "").trim();
    return (
      <button type="button" onClick={() => onOpen?.(parada, "anotaciones")} className="w-full text-left">
        {txt ? (
          <ul className="list-disc space-y-1 pl-4 text-sm text-zinc-700">
            <li className="line-clamp-4">{txt}</li>
          </ul>
        ) : (
          <p className="text-sm text-zinc-400 italic">Sin anotaciones · tocar para añadir</p>
        )}
      </button>
    );
  }
  if (categoria === "gastos") {
    const gastos = parada.gastos || [];
    return (
      <button type="button" onClick={() => onOpen?.(parada, "gastos")} className="w-full text-left">
        {gastos.length ? (
          <ul className="list-disc space-y-1 pl-4 text-sm text-zinc-700">
            {gastos.slice(0, 4).map((g) => (
              <li key={g.id}>
                {g.categoria}: {g.importe != null ? `${Number(g.importe).toFixed(2)} €` : "—"}
              </li>
            ))}
            {gastos.length > 4 && <li className="text-zinc-400">+{gastos.length - 4} más</li>}
          </ul>
        ) : (
          <p className="text-sm text-zinc-400 italic">
            {r.gastos ? `${r.gastos} gasto(s)` : "Parking, dieta, km… · tocar"}
          </p>
        )}
      </button>
    );
  }
  if (categoria === "documentos") {
    const docs = parada.adjuntos || [];
    return (
      <button type="button" onClick={() => onOpen?.(parada, "documentos")} className="w-full text-left">
        {docs.length ? (
          <ul className="list-disc space-y-1 pl-4 text-sm text-zinc-700">
            {docs.slice(0, 4).map((a) => (
              <li key={a.id} className="truncate">{a.tipo}: {a.nombre_archivo}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-zinc-400 italic">Fotos o documentos · tocar</p>
        )}
      </button>
    );
  }
  return (
    <button type="button" onClick={() => onOpen?.(parada, "ubicacion")} className="w-full text-left space-y-1">
      <p className="text-sm text-zinc-700">{parada.direccion || "Sin dirección"}</p>
      {parada.latitud != null && (
        <p className="text-[11px] text-zinc-500">
          {Number(parada.latitud).toFixed(5)}, {Number(parada.longitud).toFixed(5)}
        </p>
      )}
      {parada.km_desde_anterior?.km != null && (
        <p className="text-[11px] font-medium text-emerald-700">
          {fmtKm(parada.km_desde_anterior)} desde la anterior
        </p>
      )}
      {parada.maps_url && (
        <a
          href={parada.maps_url}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] font-medium text-sky-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Abrir en Google Maps
        </a>
      )}
    </button>
  );
}

export default function RutaRoadmapVisual({ ruta, paradaActivaId, onSelectParada, onOpenParada }) {
  const paradas = useMemo(() => paradasConDesvio(ruta?.paradas || []), [ruta?.paradas]);
  const [seleccionId, setSeleccionId] = useState(paradaActivaId || paradas[0]?.id || null);

  useEffect(() => {
    if (paradaActivaId) {
      setSeleccionId(paradaActivaId);
      return;
    }
    if (!paradas.some((p) => p.id === seleccionId)) {
      setSeleccionId(paradas[0]?.id || null);
    }
  }, [paradaActivaId, paradas, seleccionId]);

  const seleccionada = useMemo(
    () => paradas.find((p) => p.id === seleccionId) || paradas[0] || null,
    [paradas, seleccionId],
  );

  const elegir = (p) => {
    setSeleccionId(p.id);
    onSelectParada?.(p);
  };

  if (!paradas.length) {
    return (
      <p className="rounded-2xl border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
        Añade clientes a la ruta para ver el planning visual.
      </p>
    );
  }

  const colorSel = seleccionada?.estado === "visitada"
    ? "#059669"
    : (seleccionada?.pin_color || "#e11d48");

  return (
    <div className="space-y-5">
      {(ruta.km_total > 0 || (ruta.tramos || []).length > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800">
            Recorrido aprox. {Number(ruta.km_total || 0).toFixed(1)} km
          </span>
          {(ruta.tramos || []).filter((t) => t.km != null).map((t) => (
            <span key={`${t.desde_orden}-${t.hasta_orden}`} className="rounded-full bg-zinc-100 px-2.5 py-1">
              {t.desde_origen || t.desde_orden === 0
                ? `${t.desde_nombre || "Centro"}→01`
                : `${String(t.desde_orden).padStart(2, "0")}→${String(t.hasta_orden).padStart(2, "0")}`}
              : {fmtKm(t)}
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl bg-gradient-to-b from-slate-100 to-slate-50 px-3 pb-2 pt-8">
        <div
          className="relative min-w-full"
          style={{ minWidth: `${Math.max(paradas.length * 8.2, 20)}rem` }}
        >
          <div className="pointer-events-none absolute inset-x-10 top-[2.65rem] h-6 rounded-full bg-zinc-700 shadow-inner">
            <div className="absolute inset-x-4 top-1/2 h-0.5 -translate-y-1/2 border-t-2 border-dashed border-white/70" />
          </div>
          <div className="relative flex items-end justify-between gap-1 px-2 pb-8 pt-1">
            {paradas.map((p, idx) => (
              <Fragment key={p.id}>
                {idx === 0 && (p.km_desde_anterior?.km != null || p.km_desde_anterior?.desde_origen) && (
                  <div className="mb-20 flex min-w-[3.5rem] flex-col items-center justify-center">
                    <span className="max-w-[6.5rem] truncate whitespace-nowrap rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-800 shadow ring-1 ring-violet-100">
                      {fmtKm(p.km_desde_anterior) || `${p.km_desde_anterior?.minutos || "—"} min`}
                    </span>
                    <span className="mt-0.5 max-w-[6.5rem] truncate text-center text-[9px] text-violet-700/80">
                      {p.km_desde_anterior?.fuente === "primer_picado" ? "primer picado" : "centro"}
                    </span>
                  </div>
                )}
                {idx > 0 && (
                  <div className="mb-20 flex min-w-[3rem] flex-1 flex-col items-center justify-center">
                    <span className="whitespace-nowrap rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 shadow ring-1 ring-emerald-100">
                      {fmtKm(p.km_desde_anterior) || "—"}
                    </span>
                  </div>
                )}
                <div className="flex w-[7.25rem] shrink-0 flex-col items-center gap-1.5">
                  <Pin
                    n={p.orden}
                    color={p.pin_color || "#e11d48"}
                    active={seleccionada?.id === p.id}
                    realizada={p.estado === "visitada"}
                    onClick={() => elegir(p)}
                  />
                  <p className="w-full truncate text-center text-[11px] font-medium text-zinc-700">
                    {p.nombre}
                  </p>
                  <HorasPin p={p} />
                </div>
              </Fragment>
            ))}
          </div>
        </div>
        {paradas.length > 3 && (
          <p className="pb-2 text-center text-[10px] text-zinc-500">Desliza el recorrido · pulsa un cliente para ver su ficha</p>
        )}
      </div>

      {seleccionada && (
        <div className="space-y-3">
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl px-4 py-3 text-white shadow"
            style={{ backgroundColor: colorSel }}
          >
            <div>
              <p className="text-base font-semibold">
                {String(seleccionada.orden).padStart(2, "0")}. {seleccionada.nombre}
              </p>
              <p className="text-xs text-white/90">
                {seleccionada.hora_planificada || seleccionada.hora_visita || seleccionada.hora_estimada
                  ? `Planificada ${seleccionada.hora_planificada || seleccionada.hora_visita || seleccionada.hora_estimada}`
                  : "Sin hora planificada"}
                {seleccionada.hora_recalculada &&
                seleccionada.hora_recalculada !== (seleccionada.hora_planificada || seleccionada.hora_visita || seleccionada.hora_estimada)
                  ? ` · ${seleccionada.estado === "visitada" ? "Real" : "Estimada"} ${seleccionada.hora_recalculada}`
                  : ""}
                {seleccionada.estado === "visitada" ? " · Realizada" : ""}
                {seleccionada.duracion_minutos ? ` · ${seleccionada.duracion_minutos} min visita` : ""}
              </p>
            </div>
            {seleccionada.maps_url && (
              <a
                href={seleccionada.maps_url}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium hover:bg-white/30"
              >
                Google Maps
              </a>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {CATEGORIAS.map((cat) => (
              <div
                key={cat.id}
                className={`min-h-[6.5rem] rounded-2xl border bg-white p-3 shadow-sm ${
                  seleccionada.estado === "visitada" ? "border-emerald-100" : "border-zinc-100"
                }`}
              >
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white">
                  <span aria-hidden>{cat.icon}</span>
                  {cat.label}
                </div>
                <CardContenido categoria={cat.id} parada={seleccionada} onOpen={onOpenParada} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
